import { CommonModule } from '@angular/common';
import { Component, Inject, signal, computed, OnInit, inject } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';

import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';
import {
  CriteriaSelectorComponent,
  CriteriaItem,
} from '../criteria-selector/criteria-selector.component';
import { CatalogFilters, GLOBAL_INFLUENCE_FILTER_ID } from '../catalog-filters/catalog-filters.component';
import { UserAssetsService, LanguageService, OrganisationFilters, ProjectFilters } from '@gs-2.0/core';

/**
 * Data passed to the bottom sheet
 */
export interface CatalogFiltersBottomSheetData {
  filters: CatalogFilters;
  filterUpdates?: OrganisationFilters | ProjectFilters;
  filterType?: 'ORGANISATION' | 'PROJECT';
}

/**
 * Catalog filters bottom sheet component - for mobile view
 * Uses Angular Material Bottom Sheet
 */
@Component({
  selector: 'app-catalog-filters-bottom-sheet',
  standalone: true,
  imports: [
    CommonModule,
    TranslateLangPipe,
    CriteriaSelectorComponent,
  ],
  templateUrl: './catalog-filters-bottom-sheet.component.html',
  styleUrls: ['./catalog-filters-bottom-sheet.component.scss'],
})
export class CatalogFiltersBottomSheetComponent implements OnInit {
  private userAssetsService = inject(UserAssetsService);
  private languageService = inject(LanguageService);

  /**
   * Loading state
   */
  isLoading = signal<boolean>(false);

  /**
   * Sort criteria items
   */
  sortItems = signal<CriteriaItem[]>([]);

  /**
   * Address filter items (ORGANISATION only). Keyed by country name (uuid is null).
   */
  addressItems = signal<CriteriaItem[]>([]);

  /**
   * Region filter items
   */
  regionItems = signal<CriteriaItem[]>([]);

  /**
   * Country filter items
   */
  countryItems = signal<CriteriaItem[]>([]);

  /**
   * Cause filter items
   */
  causeItems = signal<CriteriaItem[]>([]);

  /**
   * Category filter items
   */
  categoryItems = signal<CriteriaItem[]>([]);

  /**
   * Champions filter items
   */
  championsItems = signal<CriteriaItem[]>([]);

  /**
   * Computed signals to determine if filters have data
   */
  hasSortItems = computed(() => this.sortItems().length > 0);
  hasAddressItems = computed(() => this.addressItems().length > 0);
  hasRegionItems = computed(() => this.regionItems().length > 0);
  hasCountryItems = computed(() => this.countryItems().length > 0);
  hasCauseItems = computed(() => this.causeItems().length > 0);
  hasCategoryItems = computed(() => this.categoryItems().length > 0);
  hasChampionsItems = computed(() => this.championsItems().length > 0);

  /**
   * Mapping for sort option values to display labels
   */
  private readonly sortOptionsMapping: Record<string, string> = {
    ORGANISATION_NAME: 'Organization Name',
    PROJECT_NAME: 'Project Name',
    DATE_POSTED: 'Posted Date',
    SCORE: 'Featured',
  };

  constructor(
    private bottomSheetRef: MatBottomSheetRef<CatalogFiltersBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: CatalogFiltersBottomSheetData
  ) {}

  ngOnInit(): void {
    // Always load from API to ensure we have all filter options including sortOptions
    this.loadFilters();
  }

  /**
   * Load filters from API based on filterType
   */
  private loadFilters(): void {
    this.isLoading.set(true);
    const currentLanguage = this.languageService.getCurrentLanguage();
    const languageCode = this.mapLanguageCode(currentLanguage.code);
    const filterType = this.data?.filterType || 'ORGANISATION';

    const filtersObservable = filterType === 'PROJECT'
      ? this.userAssetsService.getProjectFilters()
      : this.userAssetsService.getOrganisationFilters();

    filtersObservable.subscribe({
      next: (response) => {
        // First, load all filters from API (includes sortOptions)
        this.updateFiltersFromData(response.response);

        // If filterUpdates is provided, update dynamic filters (regions, countries, etc.)
        // but keep sortOptions from the API
        if (this.data?.filterUpdates) {
          this.updateDynamicFilters(this.data.filterUpdates);
        }

        // Merge with passed data if available to preserve selected states
        if (this.data?.filters) {
          this.mergeSelectedStates();
        }

        this.isLoading.set(false);
      },
      error: (error) => {
        console.error(`Error loading ${filterType.toLowerCase()} filters:`, error);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Update dynamic filters (regions, countries, etc.) from filterUpdates
   * This preserves sortOptions from the main API call
   */
  private updateDynamicFilters(filters: OrganisationFilters | ProjectFilters): void {
    // Only update dynamic filters, not sortOptions

    // Transform addresses to CriteriaItem[] (ORGANISATION only)
    this.addressItems.set(this.buildAddressItems(filters));

    // Transform regions to CriteriaItem[] (always apply)
    this.regionItems.set(
      (filters.regions || []).map((region) => ({
        id: region.uuid,
        label: region.name,
        selected: false,
      }))
    );

    // Transform countries to CriteriaItem[] (always apply), prepending the
    // synthetic "Global" option at the top of the country section.
    this.countryItems.set(this.buildCountryItems(filters));

    // Transform causes to CriteriaItem[] (always apply)
    this.causeItems.set(
      (filters.causes || []).map((cause) => ({
        id: cause.uuid,
        label: cause.name,
        selected: false,
      }))
    );

    // Transform categories to CriteriaItem[] (always apply)
    this.categoryItems.set(
      (filters.categories || []).map((category) => ({
        id: category.uuid,
        label: category.name,
        selected: false,
      }))
    );

    // Transform champions to CriteriaItem[] (always apply)
    this.championsItems.set(
      (filters.champions || []).map((champion) => ({
        id: champion.uuid,
        label: champion.name,
        selected: false,
      }))
    );
  }

  /**
   * Update filters from API data
   */
  private updateFiltersFromData(filters: OrganisationFilters | ProjectFilters): void {
    // Transform sort options to CriteriaItem[] (only if not null)
    if (filters.sortOptions) {
      this.sortItems.set(
        filters.sortOptions.map((option) => ({
          id: option,
          label: this.sortOptionsMapping[option] || option,
          selected: false,
        }))
      );
    }

    // Transform addresses to CriteriaItem[] (ORGANISATION only)
    this.addressItems.set(this.buildAddressItems(filters));

    // Transform regions to CriteriaItem[] (always apply)
    this.regionItems.set(
      (filters.regions || []).map((region) => ({
        id: region.uuid,
        label: region.name,
        selected: false,
      }))
    );

    // Transform countries to CriteriaItem[] (always apply), prepending the
    // synthetic "Global" option at the top of the country section.
    this.countryItems.set(this.buildCountryItems(filters));

    // Transform causes to CriteriaItem[] (always apply)
    this.causeItems.set(
      (filters.causes || []).map((cause) => ({
        id: cause.uuid,
        label: cause.name,
        selected: false,
      }))
    );

    // Transform categories to CriteriaItem[] (always apply)
    this.categoryItems.set(
      (filters.categories || []).map((category) => ({
        id: category.uuid,
        label: category.name,
        selected: false,
      }))
    );

    // Transform champions to CriteriaItem[] (always apply)
    this.championsItems.set(
      (filters.champions || []).map((champion) => ({
        id: champion.uuid,
        label: champion.name,
        selected: false,
      }))
    );
  }

  /**
   * Build address CriteriaItem[] from API filters (ORGANISATION only). Address options
   * are keyed by `name` (uuid is null), so the option name is used as the item id/value.
   */
  private buildAddressItems(filters: OrganisationFilters | ProjectFilters): CriteriaItem[] {
    const filterType = this.data?.filterType || 'ORGANISATION';
    if (filterType !== 'ORGANISATION') {
      return [];
    }
    return ((filters as OrganisationFilters).addresses || []).map((address) => ({
      id: address.name,
      label: address.name,
      selected: false,
    }));
  }

  /**
   * Build country CriteriaItem[] from API filters, prepending a synthetic
   * "Global" option at the top when there are countries to filter by.
   */
  private buildCountryItems(filters: OrganisationFilters | ProjectFilters): CriteriaItem[] {
    const countryOptions = (filters.countries || []).map((country) => ({
      id: country.uuid,
      label: country.name,
      selected: false,
    }));
    const filterType = this.data?.filterType || 'ORGANISATION';
    const showGlobalOption = filterType === 'ORGANISATION' && countryOptions.length > 0;
    return showGlobalOption
      ? [
          {
            id: GLOBAL_INFLUENCE_FILTER_ID,
            label: 'charity_catalog.filter.global',
            selected: false,
          },
          ...countryOptions,
        ]
      : [];
  }

  /**
   * Map 2-letter language code to 3-letter code for API
   */
  private mapLanguageCode(code: string): string {
    const mapping: Record<string, string> = {
      en: 'eng',
      de: 'deu',
      es: 'spa',
      fr: 'fra',
    };
    return mapping[code] || 'eng';
  }

  /**
   * Merge selected states from passed data with API-loaded items
   */
  private mergeSelectedStates(): void {
    if (!this.data?.filters) return;

    // Helper function to merge selected states
    const mergeItems = (apiItems: CriteriaItem[], passedItems: CriteriaItem[]): CriteriaItem[] => {
      // If passedItems is empty, return apiItems as-is (no merge needed)
      if (!passedItems || passedItems.length === 0) {
        return apiItems;
      }

      const selectedIds = new Set(passedItems.filter(item => item.selected).map(item => item.id));
      return apiItems.map(item => ({
        ...item,
        selected: selectedIds.has(item.id),
      }));
    };

    // Merge all filter arrays (only if passed data has items)
    this.sortItems.set(mergeItems(this.sortItems(), this.data.filters.sort));
    this.addressItems.set(mergeItems(this.addressItems(), this.data.filters.address));
    this.regionItems.set(mergeItems(this.regionItems(), this.data.filters.region));
    this.countryItems.set(mergeItems(this.countryItems(), this.data.filters.country));
    this.causeItems.set(mergeItems(this.causeItems(), this.data.filters.cause));
    this.categoryItems.set(mergeItems(this.categoryItems(), this.data.filters.category));
    this.championsItems.set(mergeItems(this.championsItems(), this.data.filters.champions));
  }

  /**
   * Handle sort selection change
   */
  onSortChanged(items: CriteriaItem[]): void {
    this.sortItems.set(items);
  }

  /**
   * Handle address filter selection change
   */
  onAddressChanged(items: CriteriaItem[]): void {
    this.addressItems.set(items);
  }

  /**
   * Handle region filter selection change
   */
  onRegionChanged(items: CriteriaItem[]): void {
    this.regionItems.set(items);
  }

  /**
   * Handle country filter selection change
   */
  onCountryChanged(items: CriteriaItem[]): void {
    this.countryItems.set(items);
  }

  /**
   * Handle cause filter selection change
   */
  onCauseChanged(items: CriteriaItem[]): void {
    this.causeItems.set(items);
  }

  /**
   * Handle category filter selection change
   */
  onCategoryChanged(items: CriteriaItem[]): void {
    this.categoryItems.set(items);
  }

  /**
   * Handle champions filter selection change
   */
  onChampionsChanged(items: CriteriaItem[]): void {
    this.championsItems.set(items);
  }

  /**
   * Clear all filters and apply immediately
   */
  clearAllFilters(): void {
    // Clear all filter selections
    this.sortItems.set(this.sortItems().map(item => ({ ...item, selected: false })));
    this.addressItems.set(this.addressItems().map(item => ({ ...item, selected: false })));
    this.regionItems.set(this.regionItems().map(item => ({ ...item, selected: false })));
    this.countryItems.set(this.countryItems().map(item => ({ ...item, selected: false })));
    this.causeItems.set(this.causeItems().map(item => ({ ...item, selected: false })));
    this.categoryItems.set(this.categoryItems().map(item => ({ ...item, selected: false })));
    this.championsItems.set(this.championsItems().map(item => ({ ...item, selected: false })));

    // Apply the cleared filters by closing the bottom sheet with updated filters
    this.applyFilters();
  }

  /**
   * Apply filters and close bottom sheet
   */
  applyFilters(): void {
    const filters: CatalogFilters = {
      sort: this.sortItems(),
      address: this.addressItems(),
      region: this.regionItems(),
      country: this.countryItems(),
      cause: this.causeItems(),
      category: this.categoryItems(),
      champions: this.championsItems(),
    };
    this.bottomSheetRef.dismiss(filters);
  }

  /**
   * Close bottom sheet without applying
   */
  close(): void {
    this.bottomSheetRef.dismiss();
  }
}
